/**
 * Who onboarding is allowed to hold.
 *
 * Onboarding blocks — a user it claims cannot reach the rest of the app until
 * they finish — so this answers a question about permission, not about
 * progress. Two things have to be true, and both are about *this* user rather
 * than about their data:
 *
 *  1. They carry the cohort marker the backend writes at registration. Only an
 *     account created after that shipped has one, so nobody who was already
 *     using Mi Kapital can be caught by a change here.
 *
 *  2. They are in a space of their own. Onboarding sets up *your* workspace: it
 *     asks for your country, makes you a cash account, has you add your first
 *     real one. Someone invited into another person's workspace is new to the
 *     app but did not come here to set anything up — and the previous wizard
 *     greeted exactly those people with "create your first account" inside a
 *     workspace that wasn't theirs. Blocking would no longer merely annoy them;
 *     it would trap them.
 *
 * Both fail closed: unknown or still-loading means not held.
 */
export function ownsTheirSpace({ workspaces, currentWorkspace }) {
  if (!currentWorkspace) return false;
  const here = currentWorkspace.is_personal && currentWorkspace.workspace_role === 'Owner';
  // Any workspace they don't own means someone invited them; a role other than
  // Owner — Co-Owner included — is always an invitation.
  const invited = (workspaces || []).some((w) => w.workspace_role !== 'Owner');
  return !!here && !invited;
}

export function mayBeOnboarded({ onboarding, workspaces, currentWorkspace }) {
  return !!onboarding?.cohort && ownsTheirSpace({ workspaces, currentWorkspace });
}

/**
 * Whether a user onboarding may hold is held right now: until they finish.
 *
 * Finishing is the last step's own mark (`wizardCompletedAt`), written once a
 * movement exists. It is deliberately not "every tracked step is done":
 * account, categories and tag are all done by the time the last step shows,
 * so that test let a reload on the last step walk out with an empty ledger.
 * A user who dismissed the old modal (the flag is still read) falls through.
 */
export function holdsInOnboarding({ mayHold, completed, dismissed }) {
  return !!mayHold && !completed && !dismissed;
}

/**
 * The workspace the user was invited into, while that invitation still means
 * something: null otherwise.
 *
 * The backend writes `inviteWorkspaceId` when someone accepts an invitation,
 * new to Mi Kapital or not — and nowhere else. That is what keeps this safe
 * without the cohort marker: the only users it can hold are ones who just
 * accepted an invitation, never an existing user on their own.
 *
 * It goes stale when the workspace leaves their list (the host removed them),
 * which releases them rather than holds them: the owner rules apply again.
 *
 * Fails closed like the rest: an empty list while loading finds nothing.
 */
export function invitedWorkspace({ onboarding, workspaces }) {
  const id = onboarding?.inviteWorkspaceId;
  if (id == null) return null;
  return (workspaces || []).find((w) => w.id === id) || null;
}

/**
 * Which onboarding holds this user right now: 'owner', 'guest' or null (not
 * held). The one decision the layout's redirect acts on.
 *
 *   invited, workspace still listed           → 'guest' until inviteCompletedAt
 *   no cohort marker                          → null
 *   otherwise                                 → the owner rule, unchanged
 *
 * Guest comes first: someone who began setting up their own space and then
 * accepted an invitation came for the invitation. The owner rule would not
 * hold them anyway — the invited membership makes `ownsTheirSpace` false.
 *
 * The invitation is checked before the cohort marker: an existing user who
 * accepts one gets the guest flow too (a short one — see `inviteFirstRun`).
 * Without an invitation, a user without the marker is still never held.
 *
 * Once the guest flow is finished they are free, and the owner rule is not
 * consulted: it would say the same thing (the membership is still there),
 * and "finished" must not depend on that staying true.
 */
export function onboardingVariant({ onboarding, workspaces, currentWorkspace }) {
  if (!onboarding || !currentWorkspace) return null;
  if (invitedWorkspace({ onboarding, workspaces })) {
    return onboarding.inviteCompletedAt ? null : 'guest';
  }
  if (!onboarding.cohort) return null;
  const held = holdsInOnboarding({
    mayHold: mayBeOnboarded({ onboarding, workspaces, currentWorkspace }),
    completed: !!onboarding.wizardCompletedAt,
    dismissed: !!onboarding.wizardDismissedAt,
  });
  return held ? 'owner' : null;
}
